
import { Users, Clock, UserX, AlertCircle, Plus, Eye, Sparkles, Calendar } from "lucide-react";
import Card from "../../components/Card";
import Tilt from "react-parallax-tilt";
import { useEffect, useState } from "react";
import AbsentPreviewModal from "./AbsentPreviewModal";
import { useNavigate } from "react-router-dom";
import LateEarlyModal from "./LateEarlyModal";
import UpcomingEventsModal from "./UpcomingEventsModal";
import { getSocket } from "../../socket/socket";

import {
  getEmployeesApi,
  adminDailyAttendanceApi,
  adminPendingLeavesApi,
  adminLeaveReportApi,
  getMyLeavesApi,
  getMyAttendanceApi,
  getMyTasksApi,
  getFeedsApi,
  likeFeedApi,
  unlikeFeedApi,
  addCommentApi,
} from "../../app/api";
import { toast } from "react-hot-toast";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

export default function KpiStrip() {
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [upcomingAnniversaries, setUpcomingAnniversaries] = useState([]);
  const [showEventsModal, setShowEventsModal] = useState(false);

  const [pendingLeavesCount, setPendingLeavesCount] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [presentCount, setPresentCount] = useState(0);
  const [absentCountState, setAbsentCountState] = useState(0);
  const [showLateModal, setShowLateModal] = useState(false);

  const [absentUsers, setAbsentUsers] = useState([]);
  const [lateUsers, setLateUsers] = useState([]);
  const [earlyUsers, setEarlyUsers] = useState([]);

  const [showAbsentModal, setShowAbsentModal] = useState(false);
  const [inProgressTasksCount, setInProgressTasksCount] = useState(0);
  const [onLeaveUserIds, setOnLeaveUserIds] = useState(new Set());

  const user = JSON.parse(localStorage.getItem("user"));
  const isHR = Number(user?.role_id) === 2;

  const navigate = useNavigate();

  useEffect(() => {
    loadUpcomingBirthdays();
    loadUpcomingAnniversaries();

    if (isHR) {
      loadEmployees();
      loadAttendanceAdmin();
      loadPendingLeavesAdmin();
      loadTodayApprovedLeaves();
    } else {
      loadUserMonthlyAttendance();
      loadPendingLeavesUser();
      loadUserTasks();
    }

    // ⚡ Socket Listener for Real-Time Card Updates
    const socket = getSocket();
    if (socket) {
      socket.on("attendance_updated", () => {
        if (isHR) {
          console.log("⚡ Real-time attendance update received! Reloading stats...");
          loadAttendanceAdmin();
        }
      });
    }

    const handleLeaveUpdated = () => {
      if (isHR) {
        loadPendingLeavesAdmin();
      } else {
        loadPendingLeavesUser();
      }
    };

    window.addEventListener("leave-updated", handleLeaveUpdated);
    return () => {
      window.removeEventListener("leave-updated", handleLeaveUpdated);
      // Cleanup the socket listener when dashboard unmounts
      if (socket) {
        socket.off("attendance_updated");
      }
    };
  }, []);

  const loadEmployees = async () => {
    const res = await getEmployeesApi();
    if (res?.success) setEmployeeCount(res.data.length);
  };

  const loadAttendanceAdmin = async () => {
    const today = new Date().toISOString().split("T")[0];
    const res = await adminDailyAttendanceApi(today);

    if (res?.success) {
      const present = res.data.filter(u => u.status === 1);
      const absent = res.data.filter(u => !u.status);

      let late = [];
      let early = [];

      present.forEach(u => {
        if (!u.punch_in) return;

        const [h, m] = u.punch_in.split(":").map(Number);
        const minutes = h * 60 + m;

        if (minutes > 10 * 60) late.push(u);
        if (minutes < 9 * 60 + 30) early.push(u);
      });

      setPresentCount(present.length);
      setAbsentCountState(absent.length);
      setAbsentUsers(absent);
      setLateUsers(late);
      setEarlyUsers(early);
    }
  };

  const loadTodayApprovedLeaves = async () => {
    const today = new Date().toISOString().split("T")[0];
    const res = await adminLeaveReportApi();
    if (res?.success) {
      const ids = new Set(
        (res.data || [])
          .filter(r => r.approval_status === 1 &&
            r.leave_start_date <= today &&
            r.leave_end_date >= today)
          .map(r => Number(r.employee_id))
      );
      setOnLeaveUserIds(ids);
    }
  };

  const loadUserMonthlyAttendance = async () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const res = await getMyAttendanceApi(month, year);
    if (res?.success && Array.isArray(res.data)) {
      const present = res.data.filter(r => r.status === 1).length;
      const absent = res.data.filter(r => r.status === 0).length;
      setPresentCount(present);
      setAbsentCountState(absent);
    }
  };

  const finalAbsentCount = isHR ? Math.max(0, employeeCount - presentCount) : absentCountState;

  const showInline = (list) => list.length > 0 && list.length <= 3;

  const loadPendingLeavesAdmin = async () => {
    const res = await adminPendingLeavesApi();
    if (res?.success) {
      setPendingLeavesCount(res.data?.length || 0);
    }
  };

  const loadPendingLeavesUser = async () => {
    const res = await getMyLeavesApi();
    if (res?.success) {
      const pending = res.data?.leaves?.filter(l => l.approval_status === null) || [];
      setPendingLeavesCount(pending.length);
    }
  };

  const loadUserTasks = async () => {
    const res = await getMyTasksApi();
    if (res?.success) {
      const inProgress = res.data?.filter(t => t.status === "IN_PROGRESS" || t.status === "IN PROGRESS") || [];
      setInProgressTasksCount(inProgress.length);
    }
  };

  const loadUpcomingBirthdays = async () => {
    const res = await getFeedsApi();
    if (res?.success) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const sevenDaysLater = new Date(today);
      sevenDaysLater.setDate(today.getDate() + 7);

      const filtered = res.data
        .map(f => ({ ...f, meta: (typeof f.meta === "string" && f.meta.startsWith("{")) ? JSON.parse(f.meta) : f.meta }))
        .filter(p => {
          if (p.type !== "birthday") return false;
          const eventDate = new Date(p.event_date);
          eventDate.setHours(0, 0, 0, 0);

          // Show only if Today or within the Next 7 Days
          return eventDate >= today && eventDate <= sevenDaysLater;
        });
      setUpcomingBirthdays(filtered);
    }
  };

  const loadUpcomingAnniversaries = async () => {
    const res = await getFeedsApi();
    if (res?.success) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const sevenDaysLater = new Date(today);
      sevenDaysLater.setDate(today.getDate() + 7);

      const filtered = res.data
        .map(f => ({ ...f, meta: (typeof f.meta === "string" && f.meta.startsWith("{")) ? JSON.parse(f.meta) : f.meta }))
        .filter(p => {
          if (p.type !== "anniversary" && p.type !== "anniversary_post") return false;
          const eventDate = new Date(p.event_date);
          eventDate.setHours(0, 0, 0, 0);

          // Show only if Today or within the Next 7 Days
          return eventDate >= today && eventDate <= sevenDaysLater;
        });
      setUpcomingAnniversaries(filtered);
    }
  };

  const handleLike = async (feed) => {
    // Optimistic update for UI feel
    const isLiked = feed.is_liked;
    const res = isLiked ? await unlikeFeedApi(feed.id) : await likeFeedApi(feed.id);

    if (res.success) {
      loadUpcomingBirthdays();
      loadUpcomingAnniversaries();
    } else {
      toast.error("Failed to update like");
    }
  };

  const handleComment = async (id, comment) => {
    if (!comment.trim()) return;
    const res = await addCommentApi(id, { comment });
    if (res.success) {
      toast.success("Comment added");
      loadUpcomingBirthdays();
      loadUpcomingAnniversaries();
    }
  };



  const totalPeopleCount = upcomingBirthdays.length + upcomingAnniversaries.reduce((acc, curr) => acc + (curr.meta?.users?.length || 1), 0);

  const allUpcomingEvents = [
    ...upcomingBirthdays.map(e => ({ ...e, isBday: true })),
    ...upcomingAnniversaries.map(e => ({ ...e, isAnniv: true }))
  ].sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());

  const closestEvent = allUpcomingEvents[0];

  return (
    <>
      <div className="relative z-20  mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-7xl mx-auto ">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 ">
          <Tilt>
            <div onClick={() => isHR ? navigate("/attendance/all") : navigate("/profile", { state: { tab: "attendance" } })} className="cursor-pointer hover:scale-[1.03] transition">
              <Card title={isHR ? "Present Today" : "Present Days"} value={presentCount} icon={<Clock size={16} />} bg="bg-green-100" />
            </div>
          </Tilt>

          <Tilt>
            <div onClick={() => !isHR && navigate("/profile", { state: { tab: "attendance" } })} className={`relative group ${!isHR ? 'cursor-pointer hover:scale-[1.03] transition' : ''}`}>
              <Card title={isHR ? "Absent Today" : "Absent Days"} value={finalAbsentCount} icon={<UserX size={16} />} bg="bg-purple-100" />
              {isHR && showInline(absentUsers) && (
                <div className="absolute left-2 right-2 bottom-full mb-[-142px] bg-white/90 text-gray-800 text-xs rounded-lg shadow px-3 py-2 opacity-0 group-hover:opacity-100 transition space-y-1">
                  {absentUsers.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between gap-2">
                      <span>{u.name}</span>
                      {onLeaveUserIds.has(Number(u.user_id)) && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300 whitespace-nowrap">On Leave</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {isHR && absentUsers.length > 3 && (
                <button onClick={() => setShowAbsentModal(true)}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white/70 p-1.5 rounded-full shadow">
                  <Eye size={14} />
                </button>
              )}
            </div>
          </Tilt>

          <Tilt>
            <div className="relative group h-full">
              {isHR ? (
                <>
                  <Card
                    title="Late / Early"
                    value={`${lateUsers.length} / ${earlyUsers.length}`}
                    icon={<AlertCircle size={16} />}
                    bg="bg-red-100"
                  />
                  <button
                    onClick={() => setShowLateModal(true)}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white/70 hover:bg-white p-1.5 rounded-full shadow"
                  >
                    <Eye size={14} />
                  </button>
                </>
              ) : (
                <div onClick={() => navigate("/tasks/my")} className="cursor-pointer hover:scale-[1.03] transition h-full">
                  <Card
                    title="In Progress Tasks"
                    value={inProgressTasksCount}
                    icon={<AlertCircle size={16} />}
                    bg="bg-red-100"
                  />
                </div>
              )}
            </div>
          </Tilt>

          <Tilt>
            <div onClick={() => navigate(isHR ? "/leaves/pending" : "/leaves/me")} className="cursor-pointer hover:scale-[1.03] transition">
              <Card title="Pending Leaves" value={pendingLeavesCount} icon={<Clock size={16} />} bg="bg-blue-100" />
            </div>
          </Tilt>

          <Tilt>
            <div onClick={() => setShowEventsModal(true)} className="cursor-pointer hover:scale-[1.03] transition">
              <div className="relative rounded-2xl p-[27px] bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 shadow-lg flex flex-col justify-between">

                {/* TOP LEFT ICON BOX (Professional Icon) */}
                <div className="absolute top-3 left-3 bg-white p-2.5 rounded-xl shadow-sm border border-purple-100 flex items-center justify-center">
                  {closestEvent?.isBday ? (
                    <div className="text-pink-500 animate-pulse">
                      <Plus size={18} strokeWidth={3} />
                    </div>
                  ) : closestEvent?.isAnniv ? (
                    <div className="text-purple-600 animate-pulse">
                      <Sparkles size={18} strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="text-black-300">
                      <Calendar size={18} />
                    </div>
                  )}
                </div>

                {/* COUNT BADGE */}
                {totalPeopleCount > 1 && (
                  <div className="absolute top-3 right-3 bg-gradient-to-tr from-purple-600 to-indigo-600 text-white text-[10px] px-2 py-0.5 rounded-full shadow-lg font-bold border border-white/20">
                    +{totalPeopleCount - 1}
                  </div>
                )}

                {/* CONTENT */}
                <div className="mt-[42px] ml-[-10px]">
                  {closestEvent ? (
                    <>
                      {(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const eventDate = new Date(closestEvent.event_date);
                        eventDate.setHours(0, 0, 0, 0);

                        const diffTime = eventDate.getTime() - today.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        const typeLabel = closestEvent.isBday ? "Birthday" : "Work Anniversary";
                        const icon = closestEvent.isBday ? "🎂" : "🎉";

                        if (diffDays === 0) {
                          return <p className="text-[10px] font-bold text-purple-600/70 uppercase tracking-wider">{closestEvent.isBday ? "Today's Birthday" : "Work Anniversary"} {icon}</p>;
                        }

                        return <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-[-9px]">{typeLabel} in {diffDays} Day{diffDays > 1 ? 's' : ''}</p>;
                      })()}

                      <p className="text-base font-black text-gray-800 truncate max-w-[120px] ">
                        {(() => {
                          const firstName = closestEvent?.meta?.users?.[0]?.first_name || closestEvent?.meta?.first_name || "Unknown";
                          if (totalPeopleCount > 1) {
                            return `${firstName} & ${totalPeopleCount - 1} others`;
                          }
                          return firstName;
                        })()}
                      </p>

                    </>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500">Upcoming Events</p>
                      <p className="text-xs text-gray-400 mt-0.5">No upcoming events</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Tilt>
        </div>
      </div>

      {absentUsers.length > 3 && (
        <AbsentPreviewModal isOpen={showAbsentModal} onClose={() => setShowAbsentModal(false)} users={absentUsers} onLeaveUserIds={onLeaveUserIds} />
      )}
      {(lateUsers.length + earlyUsers.length) >= 0 && (
        <LateEarlyModal isOpen={showLateModal} onClose={() => setShowLateModal(false)} lateUsers={lateUsers} earlyUsers={earlyUsers} />
      )}
      <UpcomingEventsModal
        isOpen={showEventsModal}
        onClose={() => setShowEventsModal(false)}
        birthdays={upcomingBirthdays}
        anniversaries={upcomingAnniversaries}
        onLike={handleLike}
        onComment={handleComment}
      />
    </>
  );
}
